/**
 * useAutopilot — React Query hooks for autopilot REST API.
 * Backed by server/modules/autopilot/routes.ts
 */
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";

export interface VolumeStats {
  sessionId: string;
  weekCount: number;
  monthCount: number;
  weeklyCap: number;
  monthlyCap: number;
  weeklyRemaining: number;
  monthlyRemaining: number;
  canCreateMore: boolean;
  reason: string;
}

export interface DiagnosticReport {
  sessionId: string;
  windowDays: number;
  primaryHypothesis:
    | "audience_too_narrow"
    | "creative_weak"
    | "landing_page_broken"
    | "sales_call_problem"
    | "pixel_audience_drift"
    | "insufficient_data"
    | "performing_normally";
  confidence: "low" | "medium" | "high";
  evidence: Array<{ metric: string; value: string; expected: string }>;
  recommendation: string;
  alternativeHypotheses: string[];
  metrics: {
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: number | null;
    cpm: number | null;
    days: number;
  };
}

export function useAutopilotDiagnostic(sessionId: string | null, days: number = 7) {
  // Bez stałego refetchInterval — diagnostic zależy od KPI snapshots, więc invaliduje się
  // razem z dashboard data (handled przez parent useAutopilotDashboard polling co 30s
  // + manual invalidate po mutations). 15 min staleTime żeby uniknąć flicker przy
  // window focus events.
  return useQuery<DiagnosticReport>({
    queryKey: ["autopilot-diagnostic", sessionId, days],
    queryFn: () =>
      apiRequest("GET", `/api/autopilot/sessions/${sessionId}/diagnostic?days=${days}`),
    enabled: !!sessionId,
    staleTime: 15 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useAutopilotVolumeStats(sessionId: string | null) {
  return useQuery<VolumeStats>({
    queryKey: ["autopilot-volume", sessionId],
    queryFn: () => apiRequest("GET", `/api/autopilot/sessions/${sessionId}/volume`),
    enabled: !!sessionId,
    refetchInterval: 60_000,
  });
}
import type {
  AutopilotSession,
  AutopilotDecision,
  AutopilotAlert,
  KpiSnapshot,
} from "@shared/schema";

export interface CreativeStatusSummary {
  pendingBriefsCount: number;
  failedBriefsCount: number;
  awaitingApprovalCount: number;
  readyToUploadCount: number;
  recentVariants: Array<{
    id: string;
    title: string;
    thumbnailUrl: string | null;
    mediaType: string | null;
    createdAt: string;
  }>;
}

export interface DashboardData {
  session: AutopilotSession;
  recentDecisions: AutopilotDecision[];
  pendingAlerts: AutopilotAlert[];
  recentSnapshots: KpiSnapshot[];
  creativeStatus?: CreativeStatusSummary;
}

export interface BenchmarkBand {
  p25: number | null;
  p50: number | null;
  p75: number | null;
  source: "expert" | "external" | "self_learned";
  sampleSize: number | null;
  fallbackLevel: 1 | 2 | 3 | 4;
  resolvedFrom: { industry: string; market: string };
  notes: string | null;
}

const KEY = {
  sessions: ["autopilot", "sessions"] as const,
  dashboard: (id: string) => ["autopilot", "dashboard", id] as const,
  benchmark: (params: { funnelType: string; industry: string; market: string; metric: string }) =>
    ["autopilot", "benchmark", params] as const,
};

export function useAutopilotSessions() {
  return useQuery<AutopilotSession[]>({
    queryKey: KEY.sessions,
    queryFn: () => apiRequest("GET", "/api/autopilot/sessions"),
  });
}

export function useAutopilotDashboard(sessionId: string | undefined, days = 14) {
  return useQuery<DashboardData>({
    queryKey: sessionId ? [...KEY.dashboard(sessionId), days] : ["__skip__"],
    queryFn: () => apiRequest("GET", `/api/autopilot/sessions/${sessionId}/dashboard?days=${days}`),
    enabled: !!sessionId,
    refetchInterval: 30_000, // live dashboard polls every 30s
  });
}

export function useCreateAutopilotSession() {
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      apiRequest("POST", "/api/autopilot/sessions", input) as Promise<AutopilotSession>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.sessions });
    },
  });
}

export type OnboardingStep =
  | "pending"
  | "backfill_snapshots"
  | "analyze_existing_ads"
  | "generate_strategy"
  | "generate_creatives"
  | "activating"
  | "done"
  | "failed";

export interface OnboardingProgressDto {
  step: OnboardingStep;
  message: string;
  progress: number;
  startedAt: string;
  updatedAt: string;
  finishedAt?: string;
  error?: string;
  details?: {
    snapshotsInserted?: number;
    adsAnalyzed?: number;
    strategyGenerated?: boolean;
    briefsGenerated?: number;
  };
}

export function useStartAutopilotOnboarding() {
  return useMutation({
    mutationFn: (sessionId: string) =>
      apiRequest("POST", `/api/autopilot/sessions/${sessionId}/onboarding`) as Promise<{
        ok: boolean;
        progress: OnboardingProgressDto;
      }>,
  });
}

export interface PendingStrategyDecision {
  id: string;
  sessionId: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  reasoning: string | null;
  before: Record<string, unknown> | null;
  applied: boolean;
  reverted: boolean;
  metricsSnapshot: Record<string, unknown> | null;
}

export function useAutopilotPendingDecisions(sessionId: string | null) {
  return useQuery<{ items: PendingStrategyDecision[] }>({
    queryKey: sessionId ? ["autopilot", "pending-decisions", sessionId] : ["__skip__"],
    queryFn: () => apiRequest("GET", `/api/autopilot/sessions/${sessionId}/pending-decisions`),
    enabled: !!sessionId,
  });
}

export function useApplyStrategyDecision(sessionId: string | null) {
  return useMutation({
    mutationFn: (decisionId: string) =>
      apiRequest("POST", `/api/autopilot/decisions/${decisionId}/apply`) as Promise<{
        ok: boolean;
        error?: string;
      }>,
    onSuccess: () => {
      if (sessionId) {
        queryClient.invalidateQueries({ queryKey: ["autopilot", "pending-decisions", sessionId] });
        queryClient.invalidateQueries({ queryKey: KEY.dashboard(sessionId) });
      }
    },
  });
}

export function useDismissStrategyDecision(sessionId: string | null) {
  return useMutation({
    mutationFn: ({ decisionId, reason }: { decisionId: string; reason?: string }) =>
      apiRequest("POST", `/api/autopilot/decisions/${decisionId}/dismiss`, { reason }) as Promise<{
        ok: boolean;
        error?: string;
      }>,
    onSuccess: () => {
      if (sessionId) {
        queryClient.invalidateQueries({ queryKey: ["autopilot", "pending-decisions", sessionId] });
      }
    },
  });
}

export function useAutopilotOnboardingProgress(sessionId: string | null) {
  return useQuery<OnboardingProgressDto>({
    queryKey: sessionId ? ["autopilot", "onboarding-progress", sessionId] : ["__skip__"],
    queryFn: () => apiRequest("GET", `/api/autopilot/sessions/${sessionId}/onboarding-progress`),
    enabled: !!sessionId,
    refetchInterval: (query) => {
      const data = query.state.data as OnboardingProgressDto | undefined;
      if (!data) return 1500;
      if (data.step === "done" || data.step === "failed") return false;
      return 1500;
    },
  });
}

export function useUpdateAutopilotSession(sessionId: string) {
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      apiRequest("PATCH", `/api/autopilot/sessions/${sessionId}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.sessions });
      queryClient.invalidateQueries({ queryKey: KEY.dashboard(sessionId) });
    },
  });
}

export function useSetSessionStatus(sessionId: string) {
  return useMutation({
    mutationFn: (status: "activate" | "pause" | "vacation" | "stop") =>
      apiRequest("POST", `/api/autopilot/sessions/${sessionId}/${status}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.dashboard(sessionId) });
      queryClient.invalidateQueries({ queryKey: KEY.sessions });
    },
  });
}

export function useDeleteAutopilotSession() {
  return useMutation({
    mutationFn: (sessionId: string) => apiRequest("DELETE", `/api/autopilot/sessions/${sessionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.sessions });
    },
  });
}

export function useBackfillSnapshots(sessionId: string) {
  return useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/autopilot/sessions/${sessionId}/backfill-snapshots`) as Promise<{
        snapshotsInserted: number;
        error?: string;
      }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.dashboard(sessionId) });
    },
  });
}

export function useImportMetaAds(sessionId: string) {
  return useMutation({
    mutationFn: (limit?: number) =>
      apiRequest(
        "POST",
        `/api/autopilot/sessions/${sessionId}/import-meta-ads`,
        limit ? { limit } : undefined
      ) as Promise<{ imported: number; skipped: number; error?: string }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creative-pipeline"] });
    },
  });
}

export function useGenerateBriefs(sessionId: string) {
  return useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/autopilot/sessions/${sessionId}/generate-briefs`) as Promise<{
        generated: number;
        skipped: number;
        error?: string;
      }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creative-pipeline"] });
    },
  });
}

// Mirrors server StrategyTargetType (InitialStrategyService.ts): the Meta entity a
// kill/scale recommendation points at, so the UI can act on it with one click.
export type StrategyTargetType = "ad" | "adset" | "campaign";

export interface StrategyPlan {
  generatedAt: string;
  accountState: "empty" | "data_starved" | "has_data" | "has_winners" | "fatigued";
  summary: string;
  whatToKill: Array<{
    what: string;
    why: string;
    targetType?: StrategyTargetType;
    targetId?: string;
    targetName?: string;
  }>;
  whatToScale: Array<{
    what: string;
    why: string;
    estimatedImpact: string;
    targetType?: StrategyTargetType;
    targetId?: string;
    targetName?: string;
    budgetIncreasePct?: number;
  }>;
  whatToTest: Array<{ hypothesis: string; reason: string; metric: string }>;
  whatToCreate: Array<{ creativeType: string; angle: string; reason: string }>;
  missingPieces: Array<{ item: string; whyNeeded: string }>;
  weeklyMilestones: Array<{ week: number; goal: string; keyMetric: string; targetValue: string }>;
}

export function useGenerateStrategy(sessionId: string) {
  return useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/autopilot/sessions/${sessionId}/generate-strategy`) as Promise<{
        ok: boolean;
        strategy?: StrategyPlan;
        error?: string;
      }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.dashboard(sessionId) });
    },
  });
}

export function useResolveAlert(sessionId: string) {
  return useMutation({
    mutationFn: (input: { alertId: string; decision: "approve" | "dismiss" }) =>
      apiRequest("POST", "/api/autopilot/alerts/resolve", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.dashboard(sessionId) });
    },
  });
}

export function useRevertDecision(sessionId: string) {
  return useMutation({
    mutationFn: (input: { decisionId: string; reason: string }) =>
      apiRequest("POST", "/api/autopilot/decisions/revert", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY.dashboard(sessionId) });
    },
  });
}

export function useStrategistChat() {
  return useMutation({
    mutationFn: (input: {
      sessionId: string;
      intent: "plan_this_week" | "diagnose" | "retrospective" | "benchmark_query";
      metric?: string;
    }) => apiRequest("POST", "/api/autopilot/chat", input) as Promise<{ text: string }>,
  });
}

export interface PreflightCheck {
  id: string;
  label: string;
  status: "pass" | "fail" | "warning";
  severity: "required" | "recommended";
  details: string;
  remediation?: string;
}

export interface PreflightReport {
  sessionId: string;
  ready: boolean;
  checks: ReadonlyArray<PreflightCheck>;
  summary: { passed: number; failed: number; warnings: number; required: number };
}

export function useAutopilotPreflight(sessionId: string | undefined) {
  return useQuery<PreflightReport>({
    queryKey: sessionId ? ["autopilot", "preflight", sessionId] : ["__skip__"],
    queryFn: () => apiRequest("GET", `/api/autopilot/sessions/${sessionId}/preflight`),
    enabled: !!sessionId,
    refetchInterval: 60_000,
  });
}

export interface AdminTriageSession {
  sessionId: string;
  funnelType: string;
  industry: string | null;
  market: string | null;
  status: string;
  severity: "healthy" | "degraded" | "critical";
  flags: ReadonlyArray<{ code: string; message: string }>;
}

export interface AdminTriageReport {
  overall: "healthy" | "degraded" | "critical";
  sessions: AdminTriageSession[];
  summary: { total: number; critical: number; degraded: number; healthy: number };
  generatedAt: string;
}

export function useAutopilotAdminTriage() {
  return useQuery<AdminTriageReport>({
    queryKey: ["autopilot", "admin", "triage"],
    queryFn: () => apiRequest("GET", "/api/autopilot/admin/triage"),
    refetchInterval: 60_000,
  });
}

export function useBenchmarkBand(
  params: { funnelType: string; industry: string; market: string; metric: string } | null
) {
  return useQuery<{ band: BenchmarkBand | null }>({
    queryKey: params ? KEY.benchmark(params) : ["__skip__"],
    queryFn: () => {
      const qs = new URLSearchParams(params as Record<string, string>).toString();
      return apiRequest("GET", `/api/autopilot/benchmarks?${qs}`);
    },
    enabled: !!params,
  });
}
